import React, { useCallback, useEffect, useState } from "react";
import VDBText from "@components/atoms/Text";
import { Spacer } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import LayoutContainer from "@components/layout/LayoutContainer";
import { useTheme } from "styled-components/native";
import UsersSeatsList from "./UsersSeatsList";
import {
  getDaysLeft,
  usersSeatsListCustomization,
  getMessageState,
} from "@scenes/more/MoreScreen/utils";
import { ButtonType } from "@components/atoms/Button/types";
import styled, { DefaultTheme } from "styled-components/native";
import {
  ScrollView,
  TouchableOpacity,
  Linking,
  Platform,
  DeviceEventEmitter,
} from "react-native";
import { useGetUserList, useUpdateUser } from "@apis/useGetUserList";
import Loader from "@components/atoms/Loader";
import { css } from "styled-components/native";
import { TeamMember } from "@src/types/UserList";
import { useQueryClient } from "@tanstack/react-query";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useGetUser } from "@apis/user";
import InviteUserPopUp from "./InviteUserPopUp";
import { messageType } from "@components/molecules/PopUp/types";
import PopUp from "@components/molecules/PopUp";
import { handleEmailClick } from "@utils/product/productValidators";
import { useUserAnalytics } from "../../../hooks/useAnalytics";
import { EventName, EventType, ViewType, ViewTypeEnum } from "@utils/analytics";
import PurchaseSeatsPopup from "./ManagePlan/PurchaseSeats";
import ManagePlanPopUp from "./ManagePlan/ManagePlanPopUp";
import RenderHTML from "react-native-render-html";
import { useGlobalData } from "@redux/slices/globalSlice";
import ContactUsModal from "@components/molecules/ContactUs";
import PlanStatusContainer from "./ManagePlan/components/PlanStatusContainer";
import MessageContainer from "./ManagePlan/components/MessageContainer";
import InfoCards from "./ManagePlan/components/InfoCards";
import { VDBIcons } from "@utils/VDBIcons";
import CancelRenewalPopup from "./ManagePlan/components/CancelRenewalPopup";
import { useKeepMyRenewal } from "@apis/billing";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { useFocusEffect } from "@react-navigation/native";

const UsersSeatsScreen = () => {
  const { typography, t } = useScreenOptionsHooks();
  const theme = useTheme() as any;
  const { data: userList, isLoading } = useGetUserList(1);
  const users = userList?.users || [];
  const userInfo = useUserInfo();
  const globalData = useGlobalData();
  const { trackButtonEvent, trackHyperLinkEvent } = useUserAnalytics() || {};
  const sortedUsers = [...users].sort((a, b) => {
    if (a.id === userInfo.user.id) return -1;
    if (b.id === userInfo.user.id) return 1;

    const adminDiff = Number(b?.is_admin_true) - Number(a?.is_admin_true);
    if (adminDiff !== 0) return adminDiff;

    const paidDiff = Number(b?.paid_plan_user) - Number(a?.paid_plan_user);
    if (paidDiff !== 0) return paidDiff;

    const aTime = a?.last_access_time
      ? new Date(a.last_access_time).getTime()
      : -Infinity;
    const bTime = b?.last_access_time
      ? new Date(b.last_access_time).getTime()
      : -Infinity;

    return bTime - aTime;
  });
  const currentPlan = userInfo.planDetails()?.plan;
  const upcomingPlan = userInfo.getUpcomingSubscription();
  const isUpcomingPaidPlan =
    currentPlan?.is_active &&
    !userInfo?.planDetails()?.plan?.is_trial &&
    upcomingPlan &&
    upcomingPlan?.is_paid;
  const planType = currentPlan?.name || currentPlan?.plan_type;
  const planExpiry = isUpcomingPaidPlan
    ? upcomingPlan?.end_date
    : currentPlan?.expiry;
  const { daysLeftLabel, daysLeft, daysLeftNoDate, expiryDate } = getDaysLeft(
    planExpiry
  ) as {
    daysLeftLabel: string;
    daysLeft: number;
    daysLeftNoDate: string;
    expiryDate: string;
  };
  const isExpired = daysLeft <= 0;
  const isTrial = currentPlan?.is_trial;
  const showError =
    daysLeft <=
    globalData?.subscription_config?.free_trial_banner?.error?.show_in_days
      ? true
      : false;
  const showPlanStatus =
    currentPlan?.plan_type === "saas_plan"
      ? currentPlan?.purchased_seats > 0
      : !userInfo.isEnrolledTrial();
  const showNoPlanMessage =
    isTrial &&
    userInfo.getUpcomingSubscription() === null &&
    !userInfo.isEnrolledTrial();
  const {
    message: statusMessage,
    icon: statusIcon,
    color: statusColor,
    backgroundColor: statusBackgroundColor,
    actions: statusActions,
  } = getMessageState(userInfo, theme, t, currentPlan);
  const plansData = globalData?.subscription_config;
  const [managePlanProps, setManagePlanProps] = useState<{
    type: "renewal" | "purchase";
    plan_id: string;
    additional_users: number;
  }>({
    type: "purchase",
    plan_id: "",
    additional_users: 0,
  });
  const [userId, setUserId] = useState<number | null>(null);
  const {} = useGetUser(userId as number, setUserId);
  useFocusEffect(
    useCallback(() => {
      setUserId(userInfo?.user?.id);
    }, [userInfo?.user?.id])
  );
  const columns = usersSeatsListCustomization(t);
  const { mutate: updateUserMutate, isPending: isUpdateUserLoading } =
    useUpdateUser();
  const [isInviteUserPopUpVisible, setIsInviteUserPopUpVisible] =
    useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showRemoveUserPopup, setShowRemoveUserPopup] = useState<{
    visible: boolean;
    user: TeamMember | null;
  }>({
    visible: false,
    user: null,
  });
  const [invitedEmail, setInvitedEmail] = useState("");
  const [purchaseSeatsPopupVisible, setPurchaseSeatsPopupVisible] =
    useState(false);
  const [successResponseData, setSuccessResponseData] = useState<any>(null);
  const [showPurchaseSeatsSuccessPopup, setShowPurchaseSeatsSuccessPopup] =
    useState(false);
  const [isManagePlanPopUpOpen, setIsManagePlanPopUpOpen] = useState(false);
  const [showCancelRenewalPopup, setShowCancelRenewalPopup] = useState(false);
  const [showManagePlanSuccessPopup, setShowManagePlanSuccessPopup] =
    useState(false);
  const [showPurchaseSeatsWarningPopup, setShowPurchaseSeatsWarningPopup] =
    useState(false);
  const [showSheet, setShowSheet] = useState(false);
  const handleOpen = () => {
    setShowSheet(true);
  };
  const handleCloseSheet = () => {
    setShowSheet(false);
  };
  const keepMyRenewalMutation = useKeepMyRenewal();
  const handleOpenManagePlanPopup = () => {
    setIsManagePlanPopUpOpen(true);
  };
  const handleCloseManagePlanSuccessPopup = () => {
    //@ts-ignore
    setUserId(userInfo?.user?.id);
    setShowManagePlanSuccessPopup(false);
    setIsManagePlanPopUpOpen(false);
  };
  const handleOpenPurchaseSeatsPopup = () => {
    setPurchaseSeatsPopupVisible(true);
  };
  const handleClosePurchaseSeatsPopup = () => {
    setPurchaseSeatsPopupVisible(false);
  };
  const handleClosePurchaseSeatsSuccessPopup = () => {
    setSuccessResponseData(null);
    //@ts-ignore
    setUserId(userInfo?.user?.id);
    setShowPurchaseSeatsSuccessPopup(false);
  };
  const isPaymentPending =
    userInfo.isActiveFreeTrial() && !userInfo.isEnrolledTrial()
      ? userInfo.getUpcomingSubscription()?.is_payment_pending ||
        userInfo.getUpcomingSubscription()?.is_purchase_processing
      : userInfo?.user?.plan_details?.plan?.is_payment_pending ||
        userInfo?.user?.plan_details?.plan?.is_purchase_processing;
  const isPurchasePending =
    (userInfo.getUpcomingSubscription()?.is_upcoming &&
      !userInfo.getUpcomingSubscription()?.is_paid) ||
    userInfo.getUpcomingSubscription()?.is_payment_pending ||
    userInfo?.user.plan_details?.plan?.is_payment_pending;
  const handleClosePurchaseSeatsWarningPopup = () => {
    setShowPurchaseSeatsWarningPopup(false);
  };
  const handleOpenPurchaseSeatsWarningPopup = () => {
    handleOpenPurchaseSeatsPopup();
    setShowPurchaseSeatsWarningPopup(false);
  };
  const total_users_enrolled =
    (currentPlan?.included_seats_from_spend || 0) +
    (currentPlan?.purchased_seats || 0) +
    (userInfo.isEnrolledTrial() &&
    currentPlan?.total_active_users > currentPlan?.paid_users
      ? currentPlan?.total_active_users - currentPlan?.paid_users
      : 0);
  const isGracePeriod = userInfo.isGracePeriod();
  const disableManagePlanButton = (b: any) => {
    return (
      (isGracePeriod &&
        userInfo.isUpcomingNotPaidPlan() &&
        b.action === "purchase_seats") ||
      (userInfo.isUpcomingNotPaidTrialPlan() &&
        b.action === "purchase_seats") ||
      (isPaymentPending && b.action === "purchase_seats") ||
      (!userInfo.isActiveFreeTrial() &&
        (isPaymentPending || userInfo.hasNoPlan()) &&
        currentPlan?.remaining_seats === 0 &&
        b.action === "invite_users")
    );
  };
  const getPlanDetails = (plan_duration: number) => {
    return plansData?.plans.find(
      (plan: any) => Number(plan.plan_type) === plan_duration
    );
  };
  const getAdditionalUsersCountByPlanDuration = (
    plan_duration: number,
    type: "renewal" | "purchase"
  ) => {
    const planDetails = getPlanDetails(plan_duration);
    const zenskarPricings =
      type === "renewal"
        ? userInfo?.planDetails()?.zenskar_pricings_renewal ??
          userInfo?.planDetails()?.zenskar_pricings ??
          []
        : userInfo?.planDetails()?.zenskar_pricings ?? [];
    const pricingQuantity =
      zenskarPricings?.find(
        (pricing: any) =>
          pricing.product_id === planDetails?.additional_user_plan?.id
      )?.pricing_quantity ?? [];
    return pricingQuantity.reduce(
      (sum: number, q: any) => sum + (Number(q.user_count) || 0),
      0
    );
  };

  const getValue = (key: string) => {
    switch (key) {
      case "plan_ends_in":
        return isExpired ? "Ended" : daysLeftLabel;
      case "plan_name":
        return planType;
      case "paid_users":
        return userInfo.isUpcomingPaidPlan() && userInfo.isActiveFreeTrial()
          ? userInfo.getUpcomingSubscription()?.paid_users || 0
          : userInfo?.isEnrolledTrial()
          ? total_users_enrolled
          : currentPlan?.paid_users || "-";
      case "total_active_users":
        return currentPlan?.total_active_users || "-";
      case "used_seats":
        return currentPlan?.used_seats ?? "-";
      case "remaining_seats":
        return currentPlan?.remaining_seats;
      default:
        return "-";
    }
  };
  const queryClient = useQueryClient();
  const onHeaderButton = (action: string) => {
    switch (action) {
      case "manage_users":
        setPurchaseSeatsPopupVisible(true);
        break;
      case "manage_plan":
        trackButtonEvent?.(
          EventName.BUTTON_CLICK,
          EventType.MANAGE_PLAN_BUTTON_CLICK,
          { component_type: ViewType.SCREEN_VIEW },
          ViewTypeEnum?.ACCOUNT_OVERVIEW_SCREEN_VIEW
        );
        setIsManagePlanPopUpOpen(true);
        break;
      case "purchase_seats":
        if (
          currentPlan?.plan_type !== "saas_plan" &&
          !userInfo.isEnrolledTrial() &&
          ((userInfo.isActiveFreeTrial() &&
            userInfo.getUpcomingSubscription() === null) ||
            isExpired)
        ) {
          trackButtonEvent?.(
            EventName.BUTTON_CLICK,
            EventType.MANAGE_PLAN_BUTTON_CLICK,
            { component_type: ViewType.SCREEN_VIEW },
            ViewTypeEnum?.USER_SEATS_SCREEN_VIEW
          );
          handleOpenManagePlanPopup();
        } else {
          trackButtonEvent?.(
            EventName.BUTTON_CLICK,
            EventType.PURCHASE_SEATS_BUTTON_CLICK,
            { component_type: ViewType.SCREEN_VIEW },
            ViewTypeEnum?.USER_SEATS_SCREEN_VIEW
          );
          handleOpenPurchaseSeatsPopup();
        }
        break;
      case "invite_users":
        trackButtonEvent?.(
          EventName.BUTTON_CLICK,
          EventType.INVITE_USERS_BUTTON_CLICK,
          { component_type: ViewType.POP_UP },
          ViewTypeEnum?.INVITE_USER_POP_UP
        );
        if (
          userInfo.isActiveFreeTrial() ||
          (userInfo.isPaidPlan() && currentPlan?.remaining_seats > 0)
        ) {
          setIsInviteUserPopUpVisible(true);
        } else {
          setShowPurchaseSeatsWarningPopup(true);
        }
        break;
      case "change_renewal_to_two_year":
        trackButtonEvent?.(
          EventName.BUTTON_CLICK,
          EventType.MANAGE_PLAN_BUTTON_CLICK,
          { component_type: ViewType.SCREEN_VIEW },
          ViewTypeEnum?.ACCOUNT_OVERVIEW_SCREEN_VIEW
        );
        const planId = getPlanDetails(2)?.plan_id;
        setManagePlanProps({
          type: "renewal",
          plan_id: planId,
          additional_users:
            getAdditionalUsersCountByPlanDuration(
              upcomingPlan?.plan_duration,
              "renewal"
            ) ?? 0,
        });
        setIsManagePlanPopUpOpen(true);
        break;
      case "manage_renewal":
        trackButtonEvent?.(
          EventName.BUTTON_CLICK,
          EventType.MANAGE_PLAN_BUTTON_CLICK,
          { component_type: ViewType.SCREEN_VIEW },
          ViewTypeEnum?.ACCOUNT_OVERVIEW_SCREEN_VIEW
        );
        const currentPlanDuration = userInfo.planDetails()?.plan?.plan_duration;
        const currentPlanId = getPlanDetails(currentPlanDuration)?.plan_id;
        setManagePlanProps({
          type: "renewal",
          plan_id: currentPlanId,
          additional_users:
            getAdditionalUsersCountByPlanDuration(
              upcomingPlan?.plan_duration,
              "renewal"
            ) ?? 0,
        });
        setIsManagePlanPopUpOpen(true);
        break;
      case "change_renewal_to_one_year":
        trackButtonEvent?.(
          EventName.BUTTON_CLICK,
          EventType.MANAGE_PLAN_BUTTON_CLICK,
          { component_type: ViewType.SCREEN_VIEW },
          ViewTypeEnum?.USER_SEATS_SCREEN_VIEW
        );
        const oneYearPlanId = getPlanDetails(1)?.plan_id;
        setManagePlanProps({
          type: "renewal",
          plan_id: oneYearPlanId,
          additional_users:
            getAdditionalUsersCountByPlanDuration(
              upcomingPlan?.plan_duration,
              "renewal"
            ) ?? 0,
        });
        setIsManagePlanPopUpOpen(true);
        break;
      case "keep_my_renewal":
        handleKeepMyRenewal();
        break;
      default:
        break;
    }
  };

  const handleKeepMyRenewal = () => {
    keepMyRenewalMutation.mutate(
      {
        company_id: userInfo?.user?.company_id,
      },
      {
        onSuccess: (data: any) => {
          showToast(
            undefined,
            data.data.message,
            ToastTypes.SUCCESS,
            true,
            true
          );
          queryClient.invalidateQueries({ queryKey: ["USER_LIST"] });
          //@ts-ignore
          setUserId(userInfo.user.id);
        },
        onError: (error) => {
          console.log(error);
        },
      }
    );
  };

  const handleRemoveUser = (user: TeamMember) => {
    trackButtonEvent?.(
      EventName.BUTTON_CLICK,
      EventType.REMOVE_USER_BUTTON_CLICK,
      { user_id: user.id, component_type: ViewType.SCREEN_VIEW },
      ViewTypeEnum?.USER_SEATS_SCREEN_VIEW
    );
    updateUserMutate(
      {
        userId: user.id,
        data: {
          status: "deactivated",
          ...(user.paid_plan_user && { paid_plan_user: false }),
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["USER_LIST"] });
          //@ts-ignore
          setUserId(userInfo.user.id);
          setShowRemoveUserPopup({ visible: false, user: null });
        },
      }
    );
  };

  const handleMakeAdmin = (user: TeamMember) => {
    trackButtonEvent?.(
      EventName.BUTTON_CLICK,
      EventType.MAKE_ADMIN_BUTTON_CLICK,
      { user_id: user.id, component_type: ViewType.SCREEN_VIEW },
      ViewTypeEnum?.USER_SEATS_SCREEN_VIEW
    );
    updateUserMutate(
      {
        userId: user.id,
        data: { is_company_admin: true },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["USER_LIST"] });
        },
      }
    );
  };

  const handleMakeMember = (user: TeamMember) => {
    trackButtonEvent?.(
      EventName.BUTTON_CLICK,
      EventType.MAKE_MEMBER_BUTTON_CLICK,
      { user_id: user.id, component_type: ViewType.SCREEN_VIEW },
      ViewTypeEnum?.USER_SEATS_SCREEN_VIEW
    );
    updateUserMutate(
      {
        userId: user.id,
        data: { is_company_admin: false },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["USER_LIST"] });
        },
      }
    );
  };

  const handleActivateUser = (user: TeamMember) => {
    trackButtonEvent?.(
      EventName.BUTTON_CLICK,
      EventType.GRANT_ACCESS_TO_USER_BUTTON_CLICK,
      { user_id: user.id, component_type: ViewType.SCREEN_VIEW },
      ViewTypeEnum?.USER_SEATS_SCREEN_VIEW
    );
    updateUserMutate(
      { userId: user.id, data: { paid_plan_user: true } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["USER_LIST"] });
          //@ts-ignore
          setUserId(userInfo.user.id);
        },
      }
    );
  };

  const handleLimitAccess = (user: TeamMember) => {
    trackButtonEvent?.(
      EventName.BUTTON_CLICK,
      EventType.LIMIT_ACCESS_TO_USER_BUTTON_CLICK,
      { user_id: user.id, component_type: ViewType.SCREEN_VIEW },
      ViewTypeEnum?.USER_SEATS_SCREEN_VIEW
    );
    updateUserMutate(
      { userId: user.id, data: { paid_plan_user: false } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["USER_LIST"] });
          //@ts-ignore
          setUserId(userInfo.user.id);
        },
      }
    );
  };

  const handleEvent = (event: string, user: TeamMember) => {
    switch (event) {
      case "remove_user":
        setShowRemoveUserPopup({ visible: true, user: user });
        break;
      case "make_admin":
        handleMakeAdmin(user);
        break;
      case "make_member":
        handleMakeMember(user);
        break;
      case "activate_user":
        handleActivateUser(user);
        break;
      case "limit_access":
        handleLimitAccess(user);
        break;
    }
  };

  const handleCloseInviteUserSuccessPopup = () => {
    setUserId(userInfo.user.id);
    setShowSuccessPopup(false);
  };

  const TextContent = () => {
    const message = t(
      "inviteUser.popUp.successMessage.text",
      "User {{email}} has been added successfully"
    );
    const messageParts =
      message && message.includes("{{email}}")
        ? message.toString().split("{{email}}")
        : [message];
    return message && message.includes("{{email}}") ? (
      <EmailContainer>
        <VDBText
          color={theme["core-05"]}
          type={typography["Body-M"]}
          style={{ flexWrap: "wrap" }}
        >
          {messageParts[0]}
          <VDBText
            type={typography["Body-M"]}
            color={theme["main-04"]}
            onPress={() => handleEmailClick(t, invitedEmail)}
          >
            {invitedEmail}
          </VDBText>
          {messageParts[1]}
        </VDBText>
      </EmailContainer>
    ) : (
      <VDBText color={theme["core-05"]} type={typography["Body-M"]}>
        {t(message)}
      </VDBText>
    );
  };

  const getStatusLabel = () => {
    if (isExpired) {
      if (currentPlan?.is_trial) {
        return t("account.userList.status.trialExpired.text", "Trial Expired");
      } else {
        return t("account.userList.status.expired.text", "Expired");
      }
    }
    if (isTrial) {
      return t("account.userList.status.inTrial.text", "In Trial");
    }
    return t("account.userList.status.active.text", "Active");
  };

  const getStatusColor = () => {
    if (isExpired) return theme["error-01"];
    return theme["success-01"];
  };

  const getRenewalLabel = () => {
    return t("account.userList.endsOn.text", "Ends On");
  };

  const getbuttonText = () => {
    if (userInfo.planExpiryDetails()?.bucket && !currentPlan?.is_trial) {
      if (
        !userInfo.getUpcomingSubscription() &&
        userInfo.user.is_renewal_expiry
      ) {
        return t(
          "managePlan.card.button.keepMyRenewal.text",
          "Keep My Renewal"
        );
      } else if (
        userInfo.planDetails()?.plan?.plan_duration === 1 &&
        userInfo.getUpcomingSubscription()?.plan_duration === 1
      ) {
        return t(
          "managePlan.card.button.upgradeToTwoYearPlan.text",
          "Save 15% with a 2-Year Plan"
        );
      }
    } else if (
      (userInfo.isActiveFreeTrial() &&
        userInfo.getUpcomingSubscription() === null) ||
      (isExpired && currentPlan?.is_trial)
    ) {
      return t("managePlan.card.button.managePlan.text", "Upgrade Access");
    } else if (isExpired && !currentPlan?.is_trial) {
      if (userInfo.isGracePeriod()) {
        return "";
      } else {
        return t("managePlan.card.button.purchasePlan.text", "Purchase Plan");
      }
    } else if (userInfo.isUpcomingNotPaidTrialPlan()) {
      return t("managePlan.card.button.modifyPlan.text", "Upgrade Access");
    }
    return "";
  };

  useEffect(() => {
    const listener = DeviceEventEmitter.addListener(
      "OPEN_MANAGE_PLAN_POPUP",
      ({
        type,
        plan_type,
      }: {
        type: "renewal" | "purchase";
        plan_type: number;
      }) => {
        const planId = plansData?.plans.find(
          (plan: any) => Number(plan.plan_type) === plan_type
        )?.plan_id;
        setManagePlanProps({
          type: type,
          plan_id: planId,
          additional_users: currentPlan?.purchased_seats || 0,
        });
        setIsManagePlanPopUpOpen(true);
      }
    );
    return () => listener.remove();
  }, []);

  return isLoading ? (
    <Loader />
  ) : (
    <LayoutContainer>
      <ScrollView>
        <OverLay>
          {showPlanStatus && (
            <PlanStatusContainer
              onHeaderButton={onHeaderButton}
              getbuttonText={getbuttonText}
              isExpired={isExpired}
              isTrial={isTrial}
              getStatusColor={getStatusColor}
              getStatusLabel={getStatusLabel}
              getRenewalLabel={getRenewalLabel}
              expiryDate={expiryDate}
              daysLeft={daysLeft}
              daysLeftNoDate={daysLeftNoDate}
              showError={showError}
            />
          )}

          {statusMessage && statusMessage !== "" && (
            <>
              <Spacer
                size={theme.isMobile ? SpaceType.Basic : SpaceType.Double}
              />
              <MessageContainer
                statusColor={statusColor}
                statusIcon={statusIcon}
                statusMessage={statusMessage}
                statusBackgroundColor={statusBackgroundColor}
                actions={statusActions}
                onActionPress={onHeaderButton}
              />
            </>
          )}
          {Platform.OS !== "web" && (
            <>
              <Spacer
                size={theme.isMobile ? SpaceType.Basic : SpaceType.Double}
              />
              <MessageContainer
                statusColor={theme["success-01"]}
                statusIcon={VDBIcons.WARNING}
                statusMessage={t(
                  "managePlan.mobile.subscription.text",
                  "To manage or change your plan, please open VDB App in your browser, not in the mobile app."
                )}
                statusBackgroundColor={theme["success-02"]}
              />
            </>
          )}

          <Spacer size={SpaceType.Double} />

          <InfoCards
            totalUsers={
              userInfo?.isActiveFreeTrial() && !userInfo.isEnrolledTrial()
                ? currentPlan?.total_active_users
                : getValue("paid_users")
            }
            assignedUsers={getValue("used_seats")}
            unassignedUsers={getValue("remaining_seats")}
            onHeaderButton={onHeaderButton}
            disableManagePlanButton={disableManagePlanButton}
            statsBreakdown={{
              ...(currentPlan?.plan_type !== "saas_plan" &&
                !userInfo.isEnrolledTrial() && {
                  marketplacePlan: userInfo?.isActiveFreeTrial()
                    ? currentPlan?.total_active_users
                    : currentPlan?.paid_users -
                        (currentPlan?.purchased_seats || 0) || 0,
                }),
              purchasedAdditionalUsers: currentPlan?.purchased_seats || 0,
              supplierSolutionAccess:
                currentPlan?.included_seats_from_spend || 0,
              totalUsers: userInfo?.isEnrolledTrial()
                ? total_users_enrolled
                : userInfo?.isActiveFreeTrial()
                ? currentPlan?.total_active_users
                : currentPlan?.paid_users || 0,
              ...(userInfo.isEnrolledTrial() &&
                currentPlan?.total_active_users > currentPlan?.paid_users && {
                  licensedUsers:
                    currentPlan?.total_active_users - currentPlan?.paid_users ||
                    0,
                }),
            }}
          />

          <Spacer size={SpaceType.Double} />

          {showNoPlanMessage && (
            <>
              <PromoMessageContainer>
                <RenderHTML
                  source={{
                    html: t(
                      "account.userList.paidPromoMessage.text",
                      `<div>Choose a plan anytime during your trial — it will begin once your trial ends. Save 50% on your first year for a limited time. Need help? <a href="mailto:info@vdbapp.com">Contact us</a></div>`
                    ),
                  }}
                  renderersProps={{
                    a: {
                      onPress: (_event: any, href: string) => {
                        if (href) {
                          trackHyperLinkEvent?.(
                            href,
                            ViewTypeEnum?.USER_SEATS_SCREEN_VIEW
                          );
                          Linking.openURL(href);
                        }
                      },
                    },
                  }}
                  tagsStyles={{
                    body: {
                      ...typography["Minor-R"],
                      color: theme["core-06"],
                    },
                    a: {
                      color: theme["link-static"],
                      textDecorationLine: "none",
                    },
                  }}
                />
              </PromoMessageContainer>

              <Spacer size={SpaceType.Double} />
            </>
          )}
          {isPurchasePending &&
            (currentPlan?.plan_type === "saas_plan" ||
              userInfo.isEnrolledTrial()) && (
              <>
                <MessageContainer
                  statusColor={theme["error-01"]}
                  statusIcon={VDBIcons.WARNING}
                  statusMessage={t(
                    "account.overview.card.paymentPending.purchaseSeats.message.text",
                    `<div>Your payment is pending. Need help? Contact <a href="mailto:info@vdbapp.com">info@vdbapp.com</a></div>`
                  )}
                  statusBackgroundColor={theme["error-02"]}
                />
                <Spacer
                  size={theme.isMobile ? SpaceType.Basic : SpaceType.Double}
                />
              </>
            )}
          {(currentPlan?.plan_type === "saas_plan" ||
            userInfo.isEnrolledTrial()) &&
            currentPlan?.total_active_users > currentPlan?.paid_users && (
              <>
                <MessageContainer
                  statusColor={theme["error-01"]}
                  statusIcon={VDBIcons.WARNING}
                  statusMessage={t(
                    "account.overview.card.enrolledTrial.message.text",
                    `<div>Your account is over the user limit. Purchase additional users or remove users. Need help? Contact <a href="mailto:info@vdbapp.com">info@vdbapp.com</a></div>`
                  )}
                  statusBackgroundColor={theme["error-02"]}
                />
                <Spacer
                  size={theme.isMobile ? SpaceType.Basic : SpaceType.Double}
                />
              </>
            )}
          <UsersSeatsList
            columns={columns}
            users={sortedUsers}
            handleEvent={handleEvent}
          />
          {isUpdateUserLoading ||
            (keepMyRenewalMutation.isPending && (
              <Loader backgroundColor={theme["core-02"]} opacity={0.8} />
            ))}
          {isInviteUserPopUpVisible && (
            <InviteUserPopUp
              isOpen={isInviteUserPopUpVisible}
              onClose={() => setIsInviteUserPopUpVisible(false)}
              setShowSuccessPopup={setShowSuccessPopup}
              planAvailableSeats={currentPlan?.remaining_seats || 0}
              planUsedSeats={currentPlan?.used_seats || 0}
              planTotalSeats={currentPlan?.paid_users || 0}
              setInvitedEmail={setInvitedEmail}
              isTrial={
                currentPlan?.plan_type === "Trial" || userInfo.isEnrolledTrial()
              }
            />
          )}
          <ManagePlanPopUp
            isOpen={isManagePlanPopUpOpen}
            onClose={() => setIsManagePlanPopUpOpen(false)}
            setShowSuccessPopup={setShowManagePlanSuccessPopup}
            initialManagePlanProps={managePlanProps}
            setShowCancelRenewalPopup={() => {
              setShowCancelRenewalPopup(true);
              setIsManagePlanPopUpOpen(false);
            }}
          />
          <PurchaseSeatsPopup
            isOpen={purchaseSeatsPopupVisible}
            onClose={handleClosePurchaseSeatsPopup}
            setShowSuccessPopup={setShowPurchaseSeatsSuccessPopup}
            setSuccessResponseData={setSuccessResponseData}
          />
          {showCancelRenewalPopup ? (
            <CancelRenewalPopup
              isOpen={showCancelRenewalPopup}
              onClose={() => {
                setShowCancelRenewalPopup(false);
                setUserId(userInfo.user.id);
              }}
            />
          ) : null}
          {showManagePlanSuccessPopup && (
            <PopUp
              type={messageType.Success}
              title={t(
                "managePlan.popUp.successPopUp.title.text",
                "Thank you!"
              )}
              buttons={[
                {
                  type: ButtonType.primary,
                  title: "Ok",
                  onPressAction: handleCloseManagePlanSuccessPopup,
                },
              ]}
              HtmlSource={{
                html: t(
                  "managePlan.popUp.successPopUp.description.text",
                  `<div>We've sent the <strong style="font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 800; line-height: 20px;">invoice and payment</strong> link to your email.
                It may take a few minutes to arrive.</div>
                <div style="height: 12px;"></div>
                <div>If you don't receive it within 10 minutes, please contact <a href="mailto:billing@vdbapp.com" target="_blank" style="color: #007AFF; text-decoration: none;">billing@vdbapp.com</a> for assistance.</div>`
                ),
              }}
              styleKey="Body-R"
              colorKey="core-05"
            />
          )}
          {showSuccessPopup && (
            <PopUp
              type={messageType.Success}
              title={t("inviteUser.popUp.successTitle.text", "Thank you!")}
              buttons={[
                {
                  type: ButtonType.primary,
                  title: t("inviteUser.popUp.successButton.done.text", "Done"),
                  onPressAction: handleCloseInviteUserSuccessPopup,
                },
                ...(userInfo.isEnrolledTrial() ||
                userInfo.isActiveFreeTrial() ||
                currentPlan?.remaining_seats > 0
                  ? [
                      {
                        type: ButtonType.secondary,
                        title: t(
                          "inviteUser.popUp.successButton.addMore.text",
                          "Add More Users"
                        ),
                        onPressAction: () => {
                          trackButtonEvent?.(
                            EventName.BUTTON_CLICK,
                            EventType.ADD_MORE_USERS_BUTTON_CLICK,
                            { component_type: ViewType.SCREEN_VIEW },
                            ViewTypeEnum?.USER_SEATS_SCREEN_VIEW
                          );
                          setIsInviteUserPopUpVisible(true);
                          setShowSuccessPopup(false);
                        },
                      },
                    ]
                  : []),
              ]}
              hasChildren={true}
              styleKey={""}
            >
              <TextContent />
            </PopUp>
          )}
          {showPurchaseSeatsSuccessPopup && (
            <PopUp
              type={messageType.Success}
              title={t(
                "managePlan.popUp.successPopUp.title.text",
                "Thank you!"
              )}
              buttons={[
                {
                  type: ButtonType.primary,
                  title: t(
                    "managePlan.popUp.successPopUp.button.title.text",
                    "Ok"
                  ),
                  onPressAction: handleClosePurchaseSeatsSuccessPopup,
                },
                {
                  type: ButtonType.secondary,
                  title: t(
                    "managePlan.popUp.successPopUp.button.addMore.text",
                    "Add More Users"
                  ),
                  onPressAction: () => {
                    trackButtonEvent?.(
                      EventName.BUTTON_CLICK,
                      EventType.ADD_MORE_USERS_BUTTON_CLICK,
                      { component_type: ViewType.SCREEN_VIEW },
                      ViewTypeEnum?.ACCOUNT_OVERVIEW_SCREEN_VIEW
                    );
                    setPurchaseSeatsPopupVisible(true);
                    setShowPurchaseSeatsSuccessPopup(false);
                  },
                },
              ]}
              HtmlSource={{
                html: t(successResponseData?.message),
              }}
              styleKey="Body-R"
              colorKey="core-05"
            />
          )}
          {showRemoveUserPopup.visible && (
            <PopUp
              type={messageType.Generic}
              title={t("removeUser.popUp.title.text", "Remove User")}
              HtmlSource={{
                html: t(
                  "removeUser.popUp.message.text",
                  "Are you sure you want to remove this user? Proceeding will log them out of all active sessions."
                ),
              }}
              buttons={[
                {
                  type: ButtonType.primary,
                  title: t("removeUser.popUp.button.text", "Yes"),
                  onPressAction: () => {
                    if (showRemoveUserPopup.user) {
                      handleRemoveUser(showRemoveUserPopup.user);
                    }
                  },
                  loading: isUpdateUserLoading,
                  disabled: isUpdateUserLoading,
                },
                {
                  type: ButtonType.secondary,
                  title: t("removeUser.popUp.button.text", "Dismiss"),
                  onPressAction: () =>
                    setShowRemoveUserPopup({ visible: false, user: null }),
                },
              ]}
              styleKey={"Body-R"}
            />
          )}
          {showPurchaseSeatsWarningPopup && (
            <PopUp
              type={messageType.Warning}
              title={
                disableManagePlanButton("purchase_seats")
                  ? t(
                      "account.overview.card.paymentPending.purchaseSeats.message.text",
                      `Purchased user payment is pending — please check your email to complete payment to proceed further.`
                    )
                  : t(
                      "purchaseSeatsWarning.popUp.title.text",
                      "No available user licenses. Please purchase an additional user to continue."
                    )
              }
              buttons={[
                ...(!disableManagePlanButton("purchase_seats") &&
                Platform.OS === "web"
                  ? [
                      {
                        type: ButtonType.primary,
                        title: t(
                          "purchaseSeatsWarning.purchaseUsers.popUp.button.text",
                          "Purchase Users"
                        ),
                        onPressAction: handleOpenPurchaseSeatsWarningPopup,
                      },
                    ]
                  : []),
                {
                  type: disableManagePlanButton("purchase_seats")
                    ? ButtonType.primary
                    : ButtonType.secondary,
                  title: t(
                    "purchaseSeatsWarning.dismiss.popUp.button.text",
                    "Dismiss"
                  ),
                  onPressAction: handleClosePurchaseSeatsWarningPopup,
                },
              ]}
              styleKey={"Body-R"}
              HtmlSource={null}
            />
          )}

          <NeedHelpContainer
            isPaidPlan={
              currentPlan?.plan_type === "Paid" && userInfo.isPaidPlan()
            }
          >
            {currentPlan?.plan_type === "Paid" && userInfo.isPaidPlan() && (
              <VDBText color={theme["core-06"]} type={typography["Body-R"]}>
                {t(
                  "account.userList.additionalUsersRenew.text",
                  "Additional users added now are charged only through your plan renewal date and renew with your plan."
                )}
              </VDBText>
            )}
            <HelpContainer>
              <VDBText color={theme["core-06"]} type={typography["Body-R"]}>
                {t("global.text.help")}
              </VDBText>
              <Spacer size={SpaceType.Basic} />
              <TouchableOpacity onPress={handleOpen}>
                <VDBText
                  color={theme["link-static"]}
                  type={typography["Body-M"]}
                >
                  {t("global.text.contactUs")}
                </VDBText>
              </TouchableOpacity>
              <ContactUsModal
                isOpen={showSheet}
                onClose={handleCloseSheet}
                screenName={"Account Overview"}
              />
            </HelpContainer>
          </NeedHelpContainer>
        </OverLay>
      </ScrollView>
    </LayoutContainer>
  );
};

const OverLay = styled.View`
  padding: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? SpaceType.Double : SpaceType.Big}px;
  margin-bottom: ${({ theme }) =>
    theme.isMobile || theme.isTablet
      ? Platform.OS === "ios"
        ? 100
        : Platform.OS === "android"
        ? 150
        : theme.isLargeScreen
        ? SpaceType.Double
        : 100
      : Platform.OS === "ios"
      ? 140
      : Platform.OS === "android"
      ? 50
      : 0}px;
  ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isMobile &&
    css`
      padding-left: ${SpaceType.ExtraLarge}px;
    `}
`;

const PromoMessageContainer = styled.View``;

const EmailContainer = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
`;

const NeedHelpContainer = styled.View<{ isPaidPlan: boolean }>`
  flex-direction: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isLargeScreen ? "row" : "column"};
  justify-content: ${({ isPaidPlan }: { isPaidPlan: boolean }) =>
    isPaidPlan ? "space-between" : "flex-end"};
  margin-vertical: ${SpaceType.Double}px;
  padding-top: ${SpaceType.Double}px;
  margin-right: 0;
  margin-left: 0;
  border-top-width: 1px;
  border-top-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
  ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isLargeScreen &&
    css`
      gap: ${SpaceType.Big}px;
    `}
`;

const HelpContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

export default UsersSeatsScreen;
